import { useEffect, useMemo, useRef } from 'react';

// Minimal Web Audio chimes on join/leave/surge. Default off; requires user gesture to unlock.
export default function usePresenceSound({
  presenceMap,
  enabled,
  liveCount = 0,
  playerStats,
}) {
  const audioRef = useRef(null);
  const unlockedRef = useRef(false);
  const prevIdsRef = useRef(new Set());
  const surgeRef = useRef([]);
  const ids = useMemo(() => Object.keys(presenceMap || {}), [presenceMap]);
  const prevLiveRef = useRef(0);
  const prevPlayerRef = useRef({ online: 0, idle: 0, offline: 0 });

  // Create/resume context and unlock after user gesture
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === 'undefined' || !window.AudioContext) return undefined;
    if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();

    // Try immediate resume (some browsers allow)
    audioRef.current.resume?.().catch(() => {});

    const ensureResume = () => {
      if (!audioRef.current) return;
      audioRef.current.resume().finally(() => {
        unlockedRef.current = true;
      });
    };

    const events = ['click', 'touchstart', 'keydown'];
    events.forEach((ev) => window.addEventListener(ev, ensureResume, { once: true }));
    const visHandler = () => {
      if (document.visibilityState === 'visible') ensureResume();
    };
    document.addEventListener('visibilitychange', visHandler);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, ensureResume));
      document.removeEventListener('visibilitychange', visHandler);
    };
  }, [enabled]);

  // Detect joins/leaves
  useEffect(() => {
    if (!enabled || !audioRef.current) return;
    const current = new Set(ids);
    const prev = prevIdsRef.current;
    const joins = [];
    const leaves = [];
    current.forEach((id) => {
      if (!prev.has(id)) joins.push(id);
    });
    prev.forEach((id) => {
      if (!current.has(id)) leaves.push(id);
    });

    const now = Date.now();
    if (joins.length) {
      joins.forEach(() => playTone(audioRef.current, 'join'));
      surgeRef.current.push(now);
      surgeRef.current = surgeRef.current.filter((t) => now - t <= 2000);
      if (surgeRef.current.length >= 3) {
        playTone(audioRef.current, 'surge');
        surgeRef.current = [];
      }
    }
    if (leaves.length) {
      leaves.forEach(() => playTone(audioRef.current, 'leave'));
    }
    prevIdsRef.current = current;
  }, [ids, enabled]);

  // Fallback: also react to liveCount deltas (metric-level changes)
  useEffect(() => {
    if (!enabled || !audioRef.current) return;
    const prevLive = prevLiveRef.current || 0;
    const delta = liveCount - prevLive;
    if (delta > 0) {
      playTone(audioRef.current, 'join');
      if (delta >= 3) playTone(audioRef.current, 'surge');
    } else if (delta < 0) {
      playTone(audioRef.current, 'leave');
    }
    prevLiveRef.current = liveCount;
  }, [liveCount, enabled]);

  // React to player metrics changes (online/idle/offline recent)
  useEffect(() => {
    if (!enabled || !audioRef.current) return;
    const prev = prevPlayerRef.current;
    const next = {
      online: playerStats?.online || 0,
      idle: playerStats?.idle || 0,
      offline: playerStats?.offline || 0,
    };
    const prevTotal = (prev.online || 0) + (prev.idle || 0) + (prev.offline || 0);
    const nextTotal = (next.online || 0) + (next.idle || 0) + (next.offline || 0);
    const delta = nextTotal - prevTotal;
    if (delta > 0) {
      playTone(audioRef.current, 'join');
      if (delta >= 3) playTone(audioRef.current, 'surge');
    } else if (delta < 0) {
      playTone(audioRef.current, 'leave');
    }
    prevPlayerRef.current = next;
  }, [playerStats, enabled]);
}

function playTone(ctx, type) {
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const panNode = ctx.createStereoPanner ? ctx.createStereoPanner() : null;

  // Production-level subtle volumes (short, quiet confirmations)
  const cfg =
    {
      join: { freq: 780 + Math.random() * 140, dur: 0.12, vol: 0.08, pan: (Math.random() - 0.5) * 0.3 },
      leave: { freq: 320 + Math.random() * 80, dur: 0.16, vol: 0.07, pan: (Math.random() - 0.5) * 0.25 },
      surge: { freq: 150, dur: 0.2, vol: 0.1, pan: 0 },
    }[type] || { freq: 600, dur: 0.1, vol: 0.08, pan: 0 };

  osc.type = 'sine';
  osc.frequency.setValueAtTime(cfg.freq, ctx.currentTime);
  gain.gain.setValueAtTime(cfg.vol, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + cfg.dur);
  if (panNode) panNode.pan.setValueAtTime(cfg.pan, ctx.currentTime);

  osc.connect(gain);
  if (panNode) gain.connect(panNode).connect(ctx.destination);
  else gain.connect(ctx.destination);

  osc.start();
  osc.stop(ctx.currentTime + cfg.dur);
  // Fallback to ensure playback can repeat
  osc.onended = () => {};
  setTimeout(() => {
    // no-op to allow reuse; playback guard is handled by the caller
  }, cfg.dur * 1000 + 50);
}
